import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, ArrowRight, Crown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// ============ PREMIUM UPGRADE EXPERIENCE ============
// This is NOT a sales page. This is a natural next step.
// The user should feel: "I want this" — not "I am being sold to"
// NOTE: During beta (SHOW_PRICING=false), redirect to profile-revelation

const UpgradeExperience = () => {
  const [stage, setStage] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // During beta (SHOW_PRICING=false) OR beta users: redirect to profile revelation
  useEffect(() => {
    const isBetaUser = user?.is_beta_user || user?.is_test_account || user?.subscription_status === 'beta';
    if (!SHOW_PRICING || isBetaUser) {
      navigate('/profile-revelation', { replace: true });
    }
  }, [user, navigate]);

  const nextStage = () => setStage(prev => prev + 1);
  
  const handleUpgrade = async () => {
    setLoading(true);
    try {
      // Redirect to Stripe checkout or pricing page
      navigate('/pricing?plan=experience');
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueFree = () => {
    navigate('/profile-revelation');
  };

  // If SHOW_PRICING is false, this component will redirect immediately
  // So we can show a loading state while redirecting
  if (!SHOW_PRICING) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Redirection...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] overflow-hidden">
      <AnimatePresence mode="wait">
        
        {/* ============ STAGE 1: SOFT TRANSITION ============ */}
        {stage === 1 && (
          <motion.div
            key="transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="w-16 h-16 mb-10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]/60" size={24} />
            </motion.div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-white text-2xl md:text-4xl text-center mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ce n'est que le début.
            </motion.h1>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md"
            >
              Tu viens de poser les fondations de ta vision.
            </motion.p>
            
            <motion.div
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white px-8 py-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 2: AWARENESS ============ */}
        {stage === 2 && (
          <motion.div
            key="awareness"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              La vérité
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center max-w-2xl space-y-6"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                La clarté seule ne suffit pas.
              </p>
              
              <p className="text-[#71717A] text-base leading-relaxed">
                Savoir qui tu veux devenir est un premier pas.
                <br />
                Mais sans structure, sans direction, sans accompagnement…
                <br />
                la vision reste une idée.
              </p>
              
              <p 
                className="text-[#A1A1AA] text-lg pt-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                La différence entre celles qui rêvent
                <br />
                et celles qui construisent ?
              </p>
              
              <p className="text-[#D4AF37] text-lg italic pt-2">
                L'exécution alignée.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white px-8 py-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 3: PROJECTION ============ */}
        {stage === 3 && (
          <motion.div
            key="projection"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              Imagine
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center max-w-2xl"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Dans 90 jours…
              </p>
              
              <div className="space-y-4 text-left max-w-md mx-auto">
                {[
                  "Tu sais exactement quoi dire et comment le dire.",
                  "Ton image reflète enfin qui tu es vraiment.",
                  "Tes clientes idéales te trouvent — et te choisissent.",
                  "Tu ne doutes plus. Tu avances.",
                  "Tu construis avec intention, pas par hasard."
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6 + (index * 0.15), duration: 0.4 }}
                    className="flex items-start gap-3"
                  >
                    <div className="w-1 h-1 mt-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
                    <p className="text-[#A1A1AA] text-base">{item}</p>
                  </motion.div>
                ))}
              </div>
              
              <motion.p
                initial={{ y: 15, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.5 }}
                className="text-[#D4AF37]/80 text-sm italic mt-10"
              >
                Ce n'est pas un rêve.
                <br />
                C'est ce qui arrive quand tu as les bons outils.
              </motion.p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.5 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white px-8 py-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 4: OFFER INTRODUCTION ============ */}
        {stage === 4 && (
          <motion.div
            key="offer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="w-20 h-20 mb-8 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
            >
              <Crown className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-4"
            >
              L'expérience complète
            </motion.p>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-white text-3xl md:text-5xl text-center mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Vision Lab Expérience
            </motion.h2>
            
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="text-[#71717A] text-base text-center max-w-md mb-10"
            >
              Tout ce dont tu as besoin pour transformer ta vision en réalité.
              <br />
              Sans te disperser. Sans douter. Sans perdre de temps.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/5 px-8 py-5"
              >
                Découvrir <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 5: VALUE (Transformation, not features) ============ */}
        {stage === 5 && (
          <motion.div
            key="value"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
          >
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-10"
            >
              Ce que tu reçois
            </motion.p>
            
            <div className="max-w-xl w-full space-y-6">
              {[
                {
                  title: "Clarté totale",
                  description: "Ton identité de marque générée par IA, basée sur qui tu es vraiment."
                },
                {
                  title: "Direction créative",
                  description: "Plus jamais perdue devant un écran blanc. Tu sais quoi créer et pourquoi."
                },
                {
                  title: "Contenu illimité",
                  description: "Des idées alignées avec ta vision, générées en quelques secondes."
                },
                {
                  title: "Stratégie personnalisée",
                  description: "Un plan d'action adapté à ton niveau et tes objectifs."
                },
                {
                  title: "Accompagnement IA",
                  description: "Une assistante qui te comprend et te guide à chaque étape."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + (index * 0.1), duration: 0.4 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 mt-0.5 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p 
                      className="text-white text-base mb-1"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {item.title}
                    </p>
                    <p className="text-[#71717A] text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="mt-10"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white px-8 py-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 6: PRICING ============ */}
        {stage === 6 && (
          <motion.div
            key="pricing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.p
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-[#D4AF37]/60 text-xs tracking-[0.3em] uppercase mb-8"
            >
              L'investissement
            </motion.p>
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center"
            >
              <div className="mb-6">
                <span 
                  className="text-white text-6xl md:text-7xl"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  69
                </span>
                <span className="text-[#71717A] text-xl ml-2">CHF</span>
                <span className="text-[#52525B] text-base ml-1">/ mois</span>
              </div>
              
              <p className="text-[#71717A] text-sm mb-2">
                Sans engagement. Annule quand tu veux.
              </p>
              
              <p className="text-[#52525B] text-xs">
                Moins qu'un café par jour pour transformer ta vision.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-12"
            >
              <Button
                onClick={nextStage}
                className="bg-transparent border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46] hover:text-white px-8 py-5"
              >
                Continuer <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* ============ STAGE 7: EMOTIONAL CLOSE ============ */}
        {stage === 7 && (
          <motion.div
            key="close"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-center max-w-2xl mb-12"
            >
              <p 
                className="text-white text-xl md:text-2xl leading-relaxed mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tu as deux chemins devant toi.
              </p>
              
              <div className="space-y-6 text-left max-w-md mx-auto">
                <div className="border-l-2 border-[#27272A] pl-6 py-2">
                  <p className="text-[#52525B] text-sm uppercase tracking-wide mb-2">Chemin 1</p>
                  <p className="text-[#71717A] text-base">
                    Continuer seule. Tâtonner. Espérer que ça fonctionne.
                    <br />
                    Peut-être que oui. Peut-être dans 6 mois. Peut-être jamais.
                  </p>
                </div>
                
                <div className="border-l-2 border-[#D4AF37] pl-6 py-2">
                  <p className="text-[#D4AF37] text-sm uppercase tracking-wide mb-2">Chemin 2</p>
                  <p className="text-[#A1A1AA] text-base">
                    Décider maintenant. Avoir la clarté. Avoir les outils.
                    <br />
                    Construire avec intention, dès aujourd'hui.
                  </p>
                </div>
              </div>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="text-[#D4AF37]/70 text-sm italic mt-10"
              >
                Le choix t'appartient.
              </motion.p>
            </motion.div>
            
            {/* CTAs */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="flex flex-col items-center gap-4"
            >
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="upgrade-experience"
              >
                {loading ? 'Chargement...' : 'Entrer dans Vision Lab Expérience'} 
                <Crown className="ml-3" size={18} />
              </Button>
              
              <button
                onClick={handleContinueFree}
                className="text-[#52525B] hover:text-[#71717A] text-sm transition-colors mt-2"
                data-testid="continue-free"
              >
                Continuer avec Essentiel →
              </button>
            </motion.div>
          </motion.div>
        )}
        
      </AnimatePresence>
    </div>
  );
};

export default UpgradeExperience;
